import axios from 'axios';
import { Product } from 'src/models/product.model';
import { OrderLine } from 'src/models/order-line.model';
import { formatPrice } from 'src/utilities/helpers';

const state = {
  products: [],
  orderLines: [],
  productCodes: [],
  totalProductsCount: 0,
  shoppingCartInitizalized: false,
  loadingOrderLines: false,
  validatingStock: false,
  shoppingCartTotals: {
    CouponsTax: { price: '€ 0,00', rawPrice: 0 },
    CouponsNet: { price: '€ 0,00', rawPrice: 0 },
    CouponsTotal: { price: '€ 0,00', rawPrice: 0 },
    OrderCostsTax: { price: '€ 0,00', rawPrice: 0 },
    OrderCostsNet: { price: '€ 0,00', rawPrice: 0 },
    OrderCostsTotal: { price: '€ 0,00', rawPrice: 0 },
    ShippingCostsTax: { price: '€ 0,00', rawPrice: 0 },
    ShippingCostsNet: { price: '€ 0,00', rawPrice: 0 },
    ShippingCosts: { price: '€ 0,00', rawPrice: 0 },
    OrderLinesTotalTax: { price: '€ 0,00', rawPrice: 0 },
    OrderLinesTotalNet: { price: '€ 0,00', rawPrice: 0 },
    OrderLinesTotal: { price: '€ 0,00', rawPrice: 0 },
    OrderTotal: { price: '€ 0,00', rawPrice: 0 }
  },
  shoppingCartValid: false,
  shoppingCartEmpty: false,
  minimumOrderQuantityPrice: '€ 0,00',
  minimumOrderQuantityRawPrice: 0,
  orderLinesTotalPrice: '€ 0, 00',
  orderLinesTotalRawPrice: 0,

  invalidOrderLines: [],
  totalOrderLines: 0,
  stockValid: false,
  couponCode: '',
  hasCoupon: false,
  shoppingCartTotalsFormat: [],
  shoppingCartControlTotalsFormat: [],
  // BESPOKE
  unavailableProducts: [],
  showProductsUnavailablePrompt: false,
  unavailableProductsDeleted: false
  // END BESPOKE
};

const getters = {
  productCodes (state) {
    return state.productCodes;
  },
  orderLines (state) {
    return state.orderLines;
  },
  totalProductsCount (state) {
    return state.totalProductsCount;
  },
  loadingOrderLines (state) {
    return state.loadingOrderLines;
  },
  validatingStock (state) {
    return state.validatingStock;
  },
  shoppingCartInitizalized (state) {
    return state.shoppingCartInitizalized;
  },
  productInformationEndpoint (state, getters, rootState, rootGetters) {
    const endpoint = rootGetters.productInformationEndpoint;
    const client = rootGetters.clientCode;
    const language = rootGetters.language;
    return `${endpoint}/${client}?language=${language}`;
  },
  shoppingCartTotals (state) {
    return state.shoppingCartTotals;
  },
  shoppingCartTotalsFormat (state) {
    return state.shoppingCartTotalsFormat;
  },
  shoppingCartControlTotalsFormat (state) {
    return state.shoppingCartControlTotalsFormat;
  },
  stockValid (state, getters, rootState, rootGetters) {
    // BESPOKE DEN-453
    // To check stockValid do not take rootgetters.stockLimit into account
    // since stockValid is determined per product.customBooleans.IS_OUTLET_PRODUCT
    return state.stockValid;
    // END BESPOKE DEN-453
    if (!rootGetters.stockLimit) {
      return true;
    } else {
      return state.stockValid;
    }
  },
  invalidOrderLines (state) {
    return state.invalidOrderLines;
  },
  shoppingCartEmpty (state) {
    return state.shoppingCartEmpty;
  },
  orderLinesTotal (state) {
    return state.shoppingCartTotals.OrderLinesTotal;
  },
  minimumOrderQuantity (state) {
    return state.minimumOrderQuantity;
  },
  minimumOrderQuantityPrice (state) {
    return state.minimumOrderQuantityPrice;
  },
  minimumOrderQuantityRawPrice (state) {
    return state.minimumOrderQuantityRawPrice;
  },
  orderLinesTotalPrice (state) {
    return state.orderLinesTotalPrice;
  },
  orderLinesTotalRawPrice (state) {
    return state.orderLinesTotalRawPrice;
  },
  minimumOrderQuantityValid (state, getters, rootState, rootGetters) {
    if (rootGetters.useMinimumOrderQuantity) {
      return getters.orderLinesTotalRawPrice - getters.minimumOrderQuantityRawPrice > 0;
    } else {
      return true;
    }
  },
  couponCode (state) {
    return state.couponCode;
  },
  hasCoupon (state) {
    return state.hasCoupon;
  },
  showCheckoutButton (state, getters) {
    return getters.stockValid && getters.minimumOrderQuantityValid;
  },
  // BESPOKE
  showProductsUnavailablePrompt(state) {
    return state.showProductsUnavailablePrompt;
  },
  unavailableProducts(state) {
    return state.unavailableProducts;
  },
  unavailableProductsDeleted(state) {
    return state.unavailableProductsDeleted;
  }
  // END BESPOKE
};

const mutations = {
  initConfig (state, config) {
    state.shoppingCartTotalsFormat = config.shoppingCartTotalsFormat;
    state.shoppingCartControlTotalsFormat = config.shoppingCartControlTotalsFormat;
  },
  setProductCodes (state, productCodes) {
    state.productCodes = productCodes;
  },
  setProducts (state, products) {
    const orderLines = [...state.orderLines];
    orderLines.forEach(orderLine => {
      orderLine.setProduct(products.filter(product => product.id === orderLine.productId)[0]);
      orderLine.setComputedQuantity();
    });
  },
  setOrderLines (state, orderLines) {
    state.orderLines = orderLines;
    state.totalOrderLines = orderLines.length;
  },
  setLoadingOrderLines (state, loading) {
    state.loadingOrderLines = loading;
  },
  setValidatingStock (state, validating) {
    state.validatingStock = validating;
  },
  addToCart (state, productRow) {
    state.shoppingCartRows.push(productRow);
  },
  setTotalAndCount (state, totalProducts) {
    state.totalProductsCount = totalProducts;
  },
  setShoppingCartEmpty (state, empty) {
    state.shoppingCartEmpty = empty;
  },
  setProductPrice (state, productWithPrice) {
    state.orderLines.forEach((orderLine) => {
      if (orderLine.productId === productWithPrice.id) {
        orderLine.product.prices = productWithPrice.prices;
        orderLine.setOrderLineTotal();
      }
    });
  },
  setProductStock (state, productWithStock) {
    state.orderLines.forEach(orderLine => {
      if (orderLine.productId === productWithStock.id) {
        orderLine.product.setStock(productWithStock.stock);
        orderLine.setComputedQuantity();
      }
    });
  },
  updateOrderLineQuantity (state, { lineId, quantity, unitCode, totalCount, showPrices }) {
    let index = state.orderLines.findIndex(orderLine => orderLine.lineId === lineId);
    state.orderLines[index].quantity = quantity;
    state.orderLines[index].unitCode = unitCode;

    // update total orderline quantities
    let prodCode = state.orderLines[index].productId;
    state.orderLines.forEach(orderLine => {
      if (orderLine.productId === prodCode) {
        orderLine.totalOrderLinesQuantity = totalCount;
      }
    });
    if (showPrices) {
      state.orderLines[index].setOrderLineTotal();
    }
    state.orderLines[index].setComputedQuantity();
  },
  updateOrderLineComment (state, { lineId, comment }) {
    let index = state.orderLines.findIndex(orderLine => orderLine.lineId === lineId);
    state.orderLines[index].comments = comment;
  },
  deleteOrderLine (state, { orderLineIndex, totalCount }) {
    const orderLines = [ ...state.orderLines ];
    let prodCode = state.orderLines[orderLineIndex].productId;
    state.orderLines.forEach(orderLine => {
      if (orderLine.productId === prodCode) {
        orderLine.totalOrderLinesQuantity = totalCount;
      }
    });
    orderLines.splice(orderLineIndex, 1);
    state.orderLines = orderLines;
  },
  setShoppingCartTotals (state, totals) {
    let totalsObj = {};
    // BESPOKE DEN-496
    // Take priceIncrease into account
    let getters = window.vuexStore.$store.getters;
    let priceIncrease = getters.bespokeGlobalWs.priceRaisePercentage;
    let factor = 1 + (priceIncrease / 100);
    Object.keys(totals).forEach(key => {
      totalsObj[key] = { price: formatPrice(totals[key] * factor), rawPrice: totals[key] * factor};
    });
    // END BESPOKE DEN496
    state.shoppingCartTotals = totalsObj;
    state.minimumOrderQuantityPrice = formatPrice(totals.MinimumOrderQuantity);
    state.minimumOrderQuantityRawPrice = totals.MinimumOrderQuantity;
    state.orderLinesTotalPrice = formatPrice(totals.OrderLinesTotal);
    state.orderLinesTotalRawPrice = totals.OrderLinesTotal;
  },
  setCouponCode (state, { CouponCode, HasCoupon }) {
    state.couponCode = CouponCode;
    state.hasCoupon = HasCoupon;
  },
  setShoppingCartInitizalized (state, intitialized) {
    state.shoppingCartInitizalized = intitialized;
  },
  checkStockState (state) {
    const orderLines = [...state.orderLines];
    let invalidLines = [];
    orderLines.forEach(orderLine => {
      // BESPOKE DEN-453
      // Dental does not use stock limit, but does apply a stock limit to 
      // product.customBooleans.IS_OUTLET_PRODUCT and disable the checkout buttons
      if (orderLine.product.customBooleans.IS_OUTLET_PRODUCT) {
        if (orderLine.product.stock.stockTotal < orderLine.totalOrderLinesQuantity) {
          invalidLines.push(orderLine);
        }
      }
    });
    if (invalidLines.length) {
      state.stockValid = false;
      state.invalidOrderLines = invalidLines;
    } else {
      state.stockValid = true;
      state.invalidOrderLines = [];
    }
  },
  emptyShoppingCart (state) {
    state.orderLines = [];
  },
  // BESPOKE DEN
  setUnavailableProducts(state, products) {
    state.unavailableProducts = products;
  },
  setShowProductsUnavailablePrompt(state, show) {
    state.showProductsUnavailablePrompt = show;
  },
  setUnavailableProductsDeleted(productsDeleted) {
    state.unavailableProductsDeleted = productsDeleted;
  }
  // END BESPOKE DEN
}

const actions = {
  initShoppingCart ({ getters, commit, dispatch }) {
    dispatch('getOrderLines');
    commit('setShoppingCartInitizalized', true);
  },
  getShoppingCartTotals ({ commit, rootGetters, dispatch }) {
    axios.post(rootGetters.shoppingCartTotalsEndpoint, {})
      .then(res => {
        const data = res.data.d.Totals;
        commit('setShoppingCartTotals', data.Prices);
        dispatch('renderCheckoutButton');
        if (rootGetters.useCoupons) {
          commit('setCouponCode', data.CouponInfo);
        }
      });
  },
  getShoppingCartTotalAndCount({ commit, rootGetters }) {
    axios.post(rootGetters.shoppingCartTotalAndCountEndpoint, {})
      .then(res => {
        const data = res.data.d;
        if (data.Status === 'Success') {
          commit('setTotalAndCount', data.Totals.ProductCount);
        } else {
          // TODO ERROR HANDLING
        }
      });
  },
  getOrderLines ({ commit, dispatch, rootGetters }) {
    commit('setLoadingOrderLines', true);
    axios.post(rootGetters.orderLinesEndpoint, {})
      .then(res => {
        const data = res.data.d;
        if (data.Status === 'Success') {
          dispatch('getShoppingCartTotals');
          if (data.Lines.length) {
            const productCodes = data.Lines.map(orderLine => orderLine.ProductId);
            commit('setShoppingCartEmpty', false);
            commit('setProductCodes', productCodes);
            commit('setOrderLines', data.Lines.map(orderLine => new OrderLine(orderLine)));
            dispatch('getProductInformation');
          } else {
            commit('setShoppingCartEmpty', true);
            dispatch('renderCheckoutButton');
          }
        } else if (data.Status === 'Failure') {
          dispatch('renderCheckoutButton');
          window.updateErrorMessage(data.ErrorMessage);
        }
        commit('setLoadingOrderLines', false);
      });
  },
  getProductInformation ({ getters, rootGetters, commit, dispatch }) {
    axios.post(getters.productInformationEndpoint, getters.productCodes)
      .then(res => {
        const products = res.data.map(product => new Product(product));
        commit('setProducts', products);

        // BESPOKE DEN-557
        // If product is not available, remove it from the shoppingcart and display a information message
        if (window.vue.modules.shoppingCartOverview != undefined) {
          var productsNotAvailable = products.filter(prod => prod.customBooleans.TEMP_UNAVAILABLE || prod.customBooleans.DEFINITE_UNAVAILABLE);
        
          var orderLineIds = [];

          if (productsNotAvailable.length) {
            productsNotAvailable.forEach(product => {
              var orderLine = getters.orderLines.filter(orderLine => orderLine.productId === product.id);
              if (orderLine !== null) {
                orderLineIds.push(orderLine[0].lineId);
              }
            });

            commit('setUnavailableProducts', productsNotAvailable);
            commit('setShowProductsUnavailablePrompt', true);

            var orderLinesDeleted = 0;
            var totalOrderLinesToDelete = orderLineIds.length;
            
            orderLineIds.forEach(orderLineId => {
              dispatch('deleteOrderLine', orderLineId).then(_ => {
                orderLinesDeleted++;
                if (orderLinesDeleted === totalOrderLinesToDelete) {
                  commit('setUnavailableProductsDeleted', true);
                }
              });
            })
          }
        }
        // END BESPOKE DEN
        
        if (rootGetters.showStock) {
          dispatch('getProductStock', products);
        } else {
          products.forEach(product => { product.setStock({ stockTotal: 0 }); })
        }

        if (rootGetters.showPrices) {
          dispatch('getProductPrices', products);
        }
      });
  },
  validateShoppingCartStock ({ commit, getters, rootGetters, dispatch }) {
    commit('setValidatingStock', true);
    const products = getters.orderLines.map(orderLine => orderLine.product);

    // BESPOKE DENTAL-22
    // Implement bulk stock API instead of seperate calls
    // BESPOKE DEN-453 - Removed stockLimit check, since Dental does not use a stock limit,
    axios.post(rootGetters.scProductStockEndpoint, { productCodes: products.map(x => x.id)})
      .then(res => {
        var stock = res.data.d;
        products.forEach(product => {
          product.setStock({ stockTotal: stock[product.id] });
          commit('setProductStock', product);
        });

        commit('checkStockState');
        if (getters.invalidOrderLines.length) {
          const message = window.vue.translations.message.message_invalid_stock_orderlines;
          const offset = window.$('#stock-warning').offset().top - 75;
          window.updateErrorMessage(message);
          window.scrollTo({ top: offset, behavior: 'smooth' });
          dispatch('renderCheckoutButton');
        } else {
          dispatch('checkout');
        }
        commit('setValidatingStock', false);
    });
    // END BESPOKE DENTAL-22
  },
  getProductStock ({ rootGetters, getters, commit, dispatch }, products) {
    // BESPOKE DENTAL-22
    // Implement bulk stock API instead of seperate calls
    // BESPOKE DEN-453 - Removed stockLimit check, since Dental does not use a stock limit, 
    axios.post(rootGetters.scProductStockEndpoint, { productCodes: products.map(x => x.id)})
      .then(res => {
        var stock = res.data.d;
        products.forEach(product => {
          product.setStock({ stockTotal: stock[product.id] });
          commit('setProductStock', product);
        });

        commit('checkStockState');
        dispatch('renderCheckoutButton'); 
    });
    // END BESPOKE DENTAL-22
  },
  getProductPrices ({ commit, getters, dispatch, rootGetters }, products) {
    // BESPOKE DEN - Get own product prices first
    const productCodes = products.map(p => p.id);

    axios.post('/Bespoke/WebMethods/DentalWebMethods.aspx/GetOwnProductPrices', { productCodes: productCodes })
      .then(ownProductsRes => {
        const ownProductPrices = ownProductsRes.data.d;

        // Apply own product prices
        Object.keys(ownProductPrices).forEach(productCode => {
          let product = products.find(p => p.id === productCode);
          if (product) {
            const ownPrice = ownProductPrices[productCode];
            const priceIncrease = rootGetters.bespokeGlobalWs.priceRaisePercentage;

            // Set price as a single volume price (quantity 1)
            const pricesObj = [{
              price: ownPrice,
              basePrice: ownPrice,
              isSalesAction: false,
              quantity: 1
            }];

            product.setPrices(pricesObj, priceIncrease);
            commit('setProductPrice', product);
          }
        });

        // Filter out own products for pricing API call
        const nonOwnProducts = products.filter(p => !ownProductPrices.hasOwnProperty(p.id));

        // Only call pricing API if there are non-own products
        if (nonOwnProducts.length > 0) {
          let priceRequestWrapper = {};
          priceRequestWrapper.CustomerId = rootGetters.userLoggedOn ? rootGetters.customerId : '';
          priceRequestWrapper.Pricelist = rootGetters.customerPriceList;
          priceRequestWrapper.Products = nonOwnProducts.map(prod => {
            let ret = {};
            ret.ProductId = prod.id;
            ret.ProductGroup = prod.discountGroup;

            ret.ProductUnit = '';
            if (prod.units !== undefined && prod.units !== null) {
              ret.ProductUnit = prod.units.length > 0 ? prod.units[0].code : '';
            }
            return ret;
          });

          let endpoint = rootGetters.productPriceEndpoint + 'prices/' + rootGetters.clientCode

          axios.post(endpoint, priceRequestWrapper)
            .then(res => {
              res.data.forEach(price => {
                let product = nonOwnProducts[nonOwnProducts.findIndex(x => x.id === price.productId)];

                const pricesObj = [];
                Object.keys(price.volumes).forEach(key => {
                  pricesObj.push({
                    price: price.volumes[key].price,
                    basePrice: price.volumes[key].basePrice,
                    isSalesAction: price.volumes[key].isSalesAction,
                    quantity: Math.round(parseInt(key))
                  });
                });

                const priceIncrease = rootGetters.bespokeGlobalWs.priceRaisePercentage;
                product.setPrices(pricesObj, priceIncrease);

                commit('setProductPrice', product);
              });
            });
        }
      });
    // END BESPOKE DEN
  },
  updateOrderLineQuantity ({ rootGetters, commit, dispatch }, orderLine) {
    return new Promise((resolve, reject) => {
      axios.post(rootGetters.updateOrderLineEndpoint, { orderLineToBeUpdatedObj: orderLine })
        .then(res => {
          const data = res.data.d;
          if (data.Status === 'Success') {
            commit('updateOrderLineQuantity', {
              lineId: data.UpdatedLine.LineId,
              quantity: parseInt(data.UpdatedLine.Quantity),
              unitCode: data.UpdatedLine.UnitCode,
              totalCount: data.UpdatedLine.TotalCount,
              showPrices: rootGetters.showPrices
            });
            commit('setShoppingCartTotals', data.Totals.Prices);
            dispatch('getShoppingCartTotalAndCount');

            // BESPOKE DEN-453
            // Removed stockLimit check, since Dental does not use a stock limit, 
            // But products with customBooleans.ISOUTLET_PRODUCT do have a stock limit
            if (true) {
              commit('checkStockState');
            }

            dispatch('renderCheckoutButton');

            resolve(true);
          } else {
            reject();
            window.updateErrorMessage(data.ErrorMessage);
            dispatch('getOrderLines');
          }
        });
    });
  },
  updateOrderLineComment ({ rootGetters, commit }, orderLine) {
    return new Promise((resolve, reject) => {
      axios.post(rootGetters.updateOrderLineEndpoint, { orderLineToBeUpdatedObj: orderLine })
        .then(res => {
          const data = res.data.d;
          if (data.Status === 'Success') {
            commit('updateOrderLineComment', { lineId: data.UpdatedLine.LineId, comment: data.UpdatedLine.Comments });
            resolve();
          } else {
            reject();
            window.updateErrorMessage(data.ErrorMessage);
          }
        });
    });
  },
  deleteOrderLine ({ rootGetters, getters, commit, dispatch }, orderLineId) {    
    // BESPOKE DEN-457
    // Added promise so we can intercept from shoppingcart overview component
    // when a product has been deleted, in the case a product was swapped
    return new Promise((resolve, reject) => {
      const orderLineIndex = getters.orderLines.findIndex(orderLine => orderLine.lineId === orderLineId);
      const orderLine = getters.orderLines[orderLineIndex];
      const payload = {
        LineId: orderLine.lineId,
        ShoppingCartId: orderLine.shoppingCartId,
        ProductId: orderLine.productId,
        Comments: orderLine.comments,
        Quantity: orderLine.quantity,
        UnitCode: orderLine.unitCode,
        IsFree: orderLine.isFree
      }
      axios.post(rootGetters.deleteOrderLineEndpoint, { lineToBeDeletedObj: payload }).then(res => {
        const data = res.data.d;
        if (data.Status === 'Success') {

          // BESPOKE DEN
          // Added orderLineIndexBespoke
          // Because sometimes we perform multiple deleteOrderLines in a row, for example 
          // if multiple products are not available in the shoppingcart, we need to make sure
          // we always get the ost recent orderLineIndex between asynchronous calls
          const orderLineIndexBespoke = getters.orderLines.findIndex(orderLine => orderLine.lineId === orderLineId);
          commit('deleteOrderLine', {
            orderLineIndex: orderLineIndexBespoke,
            totalCount: data.LineToBeDeleted.TotalCount
          });
          // END BESPOKE DEN
          commit('setTotalAndCount', data.TotalsCount.ProductCount);
          commit('setShoppingCartTotals', data.Totals.Prices);
          // BESPOKE DEN-453
            // Removed stockLimit check, since Dental does not use a stock limit, 
            // But products with customBooleans.ISOUTLET_PRODUCT do have a stock limit
          if (true) {
            commit('checkStockState');
            dispatch('renderCheckoutButton');
          }
          if (getters.orderLines.length === 0) {
            commit('setShoppingCartEmpty', true);
          }
          // BESPOKE DEN-457
          resolve(true);
          // END BESPOKE DEN-457
        } else if (data.Status === 'Failure') {
          window.updateErrorMessage(data.ErrorMessage);
        }
      });
    });
    // END BESPOKE DEN-457
  },
  applyCoupon ({ rootGetters, commit }, payload) {
    return new Promise((resolve, reject) => {
      axios.post(rootGetters.addCouponEndpoint, { couponCode: payload })
        .then(res => {
          const response = res.data.d;
          const status = response.Status;
          if (status === 'Success') {
            commit('setCouponCode', { CouponCode: payload, HasCoupon: true });
            commit('setShoppingCartTotals', response.Totals.Prices);
            resolve(response.Message);
          } else if (status === 'CouponUsed' || status === 'NotFound') {
            commit('setCouponCode', { CouponCode: '', HasCoupon: false });
            reject(response.Message);
          }
        });
    });
  },
  removeCoupon ({ rootGetters, getters, commit }) {
    return new Promise((resolve, reject) => {
      axios.post(rootGetters.removeCouponEndpoint, { couponCode: getters.couponCode })
        .then(res => {
          const response = res.data.d;
          if (response.Status === 'Success') {
            commit('setCouponCode', { CouponCode: '', HasCoupon: false });
            commit('setShoppingCartTotals', response.Totals.Prices);
            resolve(response.Message);
          } else if (response.Status === 'Failure') {
            window.updateErrorMessage(response.Message);
            reject();
          }
        });
    });
  },
  emptyShoppingCart ({ rootGetters, commit }) {
    axios.post(rootGetters.emptyShoppingCartEndpoint, {}).then(res => {
      const data = res.data.d;
      if (data.Status === 'Success') {
        commit('emptyShoppingCart');
        commit('setShoppingCartEmpty', true);
        window.updateOrderMessage(data.Message);
      } else if (data.Status === 'Failure') {
        window.updateErrorMessage(data.Message);
      }
    });
  },
  renderCheckoutButton ({ getters }) {
    if (!getters.showCheckoutButton) {
      window.$('.checkout-button').addClass('disabled');
    } else {
      window.$('.checkout-button').removeClass('disabled');
    }
  },
  hidePageElements () {
    window.$('.vuejs-hook').hide();
  },
  checkout () {
    window.$('.uc_payment .inputbutton').click();
  },
  addToCart ({ rootGetters, dispatch }, payload) {
    let endpoint = rootGetters.orderProductEndpoint;
    let userLoggedOn = rootGetters.userLoggedOn;
    const products = Array.isArray(payload) ? payload : [payload];
    return new Promise((resolve, reject) => {
      axios.post(endpoint, JSON.stringify({ products: products, loggedInStatus: userLoggedOn })).then(
        res => {
          const response = res.data.d;
          if (response.errorMessages) {
            if (response.errorMessages === 'log in status changed') {
              window.location.replace('/webshop/login.aspx?RedirectUrl=' + window.location.pathname + window.location.search);
            } else {
              window.updateErrorMessage(response.errorMessages);
            }
          } else {
            resolve(true);
            dispatch('getShoppingCartTotalAndCount');

            // REFERS TO FUNCTION CALLS IN CUSTOM.JS
            if (response.errorMessages) {
              window.updateErrorMessage(response.errorMessages);
            }

            var message = response.order_message;
            if (response.totalProductsAdded > 0) {
              window.updateOrderMessage(message);
            }
            // END CUSTOM.JS FUNCTION CALLS
          }
        })
        .catch(err => {
          // @TODO Error handling
          reject(err);
        });
    });
  },
  // BESPOKE DEN-457
  reloadShoppingCartLines ({commit, dispatch}) {
    commit('setOrderLines', []);
    dispatch('getOrderLines');
  },
  confirmDeletedProducts({ commit }) {
    commit('setShowProductsUnavailablePrompt', false);
  }
  // END BESPOKE DEN-457
};

export default {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations
};
